import type { ReactElement } from 'react';

import { appStore } from '../app/store.js';
import type { Banner } from '../app/store.js';

const TONE_CLASS: Record<Banner['tone'], string> = {
  error: 'border-sell/50 bg-[#2a1215]',
  warning: 'border-warning/50 bg-[#2a2110]',
  info: 'border-accent/50 bg-[#111c2e]',
  success: 'border-buy/40 bg-[#0f2419]',
};

const TONE_ICON: Record<Banner['tone'], string> = {
  error: '✕',
  warning: '⚠',
  info: 'ℹ',
  success: '✓',
};

/**
 * Messages from the platform, stacked in the corner.
 *
 * A rejection is a banner **and** an inline note on the ticket: the banner is
 * what a trader sees if they looked away, the inline note is what they read
 * before correcting. Both carry the machine code in a muted line, because a
 * dealer on a screen share will ask for it.
 */
export function BannerStack({ banners }: { readonly banners: readonly Banner[] }): ReactElement | null {
  if (banners.length === 0) return null;
  return (
    <div className="pointer-events-none fixed right-2 bottom-8 z-50 flex w-[380px] flex-col gap-1">
      {banners.map((banner) => (
        <div
          key={banner.id}
          className={`pointer-events-auto rounded border px-2 py-1.5 shadow-lg ${TONE_CLASS[banner.tone]}`}
          role={banner.tone === 'error' ? 'alert' : 'status'}
          data-testid="banner"
        >
          <div className="flex items-start gap-2">
            <span className="mt-0.5 text-2xs">{TONE_ICON[banner.tone]}</span>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold">{banner.title}</p>
              {banner.detail.length === 0 ? null : (
                <p className="muted text-2xs">{banner.detail}</p>
              )}
              {banner.code === null ? null : (
                <p className="faint num mt-0.5 text-2xs">{banner.code}</p>
              )}
            </div>
            <button
              type="button"
              className="btn btn-ghost h-4 px-1 text-2xs"
              aria-label={`Dismiss “${banner.title}”`}
              onClick={() => appStore.dismissBanner(banner.id)}
            >
              ×
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
